import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import placeholderSvg from '../../assets/placeholder-service.svg';
import {
  logServiceProviderImageRenderIssue,
  recoverServiceProviderImageDownloadUrl,
} from '../../utils/serviceProviderImage';
import type { ServiceProviderProfileImageVariantName } from '../../types/firestore';

interface ServiceProviderImageProps {
  src?: string | null;
  storagePath?: string | null;
  alt: string;
  isTestData?: boolean;
  className?: string;
  loading?: 'eager' | 'lazy';
  diagnosticContext?: {
    component?: string;
    providerId?: string | null;
    variant?: ServiceProviderProfileImageVariantName;
  };
  onRecoveredSrc?: (url: string) => void;
  onFallback?: () => void;
}

const ServiceProviderImage: React.FC<ServiceProviderImageProps> = ({
  src,
  storagePath,
  alt,
  isTestData = false,
  className = '',
  loading = 'lazy',
  diagnosticContext,
  onRecoveredSrc,
  onFallback,
}) => {
  const [resolvedSrc, setResolvedSrc] = useState<string>(placeholderSvg);
  const recoveryAttemptRef = useRef<string | null>(null);
  const mountedRef = useRef(true);

  const normalizedSrc = useMemo(
    () => (typeof src === 'string' && src.trim().length > 0 ? src.trim() : null),
    [src]
  );
  const normalizedStoragePath = useMemo(
    () =>
      typeof storagePath === 'string' && storagePath.trim().length > 0 ? storagePath.trim() : null,
    [storagePath]
  );

  const recoverFromStoragePath = useCallback(
    async (reason: 'missing-url' | 'broken-url', originalUrl?: string | null) => {
      if (!normalizedStoragePath) {
        logServiceProviderImageRenderIssue({
          ...diagnosticContext,
          reason,
          originalUrl: originalUrl || normalizedSrc,
          storagePath: null,
        });
        return false;
      }

      const recoveryKey = `${normalizedStoragePath}:${reason}`;
      if (recoveryAttemptRef.current === recoveryKey) {
        return false;
      }
      recoveryAttemptRef.current = recoveryKey;

      const recoveredUrl = await recoverServiceProviderImageDownloadUrl(normalizedStoragePath, {
        ...diagnosticContext,
        reason,
        originalUrl: originalUrl || normalizedSrc,
      });
      if (!recoveredUrl || !mountedRef.current) {
        return false;
      }

      setResolvedSrc(recoveredUrl);
      onRecoveredSrc?.(recoveredUrl);
      return true;
    },
    [diagnosticContext, normalizedSrc, normalizedStoragePath, onRecoveredSrc]
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    recoveryAttemptRef.current = null;

    if (isTestData) {
      setResolvedSrc(placeholderSvg);
      onFallback?.();
      return;
    }

    setResolvedSrc(normalizedSrc || placeholderSvg);
    if (!normalizedSrc && normalizedStoragePath) {
      void recoverFromStoragePath('missing-url').then((recovered) => {
        if (!recovered && mountedRef.current) {
          setResolvedSrc(placeholderSvg);
          onFallback?.();
        }
      });
    } else if (!normalizedSrc) {
      onFallback?.();
    }
  }, [isTestData, normalizedSrc, normalizedStoragePath, recoverFromStoragePath, onFallback]);

  const handleImageError = useCallback(() => {
    if (isTestData) {
      setResolvedSrc(placeholderSvg);
      return;
    }

    const failedSrc = resolvedSrc;
    void recoverFromStoragePath('broken-url', failedSrc).then((recovered) => {
      if (!recovered && mountedRef.current) {
        setResolvedSrc(placeholderSvg);
        onFallback?.();
      }
    });
  }, [isTestData, onFallback, recoverFromStoragePath, resolvedSrc]);

  return (
    <img
      src={resolvedSrc}
      alt={alt}
      loading={loading}
      decoding="async"
      onError={handleImageError}
      className={className}
      data-storage-path={normalizedStoragePath || undefined}
    />
  );
};

export default ServiceProviderImage;
